/**
 * Forgot password — step 2 (code + new password). Same API as the mobile app.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import {
  recoverPassword,
  resendPasswordResetCode,
} from "../../../redux/api/auth";
import { FORGOT_PASSWORD_EMAIL_SESSION_KEY } from "../../../utils/forgotPasswordSession";

const RESEND_SECONDS = 59;

function ForgotPasswordConfirm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(FORGOT_PASSWORD_EMAIL_SESSION_KEY);
      if (!stored) {
        navigate("/authentication/forgot-password", { replace: true });
        return;
      }
      setEmail(stored);
    } catch (e) {
      navigate("/authentication/forgot-password", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const id = setInterval(() => {
      setTimer((t) => (t <= 0 ? 0 : t - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const resetTimer = () => setTimer(RESEND_SECONDS);

  const handleResend = async () => {
    if (timer !== 0 || !email) return;
    setResendLoading(true);
    resetTimer();
    try {
      const { ok, response } = await resendPasswordResetCode(email);
      if (!ok) {
        setErrorMessage(
          response?.message || "Could not resend the code. Please try again later."
        );
        setErrorOpen(true);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (code.length !== 6 || password.length < 8 || password !== confirmPassword) {
      return;
    }
    setLoading(true);
    try {
      const { ok, response } = await recoverPassword({ email, code, password });
      if (ok) {
        try {
          sessionStorage.removeItem(FORGOT_PASSWORD_EMAIL_SESSION_KEY);
        } catch (e) {
          // ignore
        }
        setSuccessOpen(true);
        setTimeout(() => {
          navigate("/authentication/sign-in", { replace: true });
        }, 1200);
        return;
      }
      setErrorMessage(
        response?.message || "Wrong verification code or the request could not be completed."
      );
      setErrorOpen(true);
    } catch (e) {
      setErrorMessage("Something went wrong. Please try again.");
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const passwordsMismatch =
    confirmPassword.length > 0 &&
    password.length === confirmPassword.length &&
    password !== confirmPassword;

  const disabled =
    !email ||
    code.length !== 6 ||
    password.length < 8 ||
    password !== confirmPassword ||
    loading;

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translate(-50%, -60%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={require("../../../assets/images/logos/mini_logo.png")}
            style={{ height: 70, width: 70, borderRadius: 10 }}
            alt=""
          />
        </div>
        <MDBox mb={2} />
        <MDBox pt={4} pb={3} px={3}>
          <MDBox mb={1}>
            <MDTypography variant="caption" color="text">
              Step 2 of 2
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="h5" fontWeight="medium">
              Enter verification code
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              We sent a code to your email. Enter it below, then choose a new password (at least 8
              characters).
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="text"
              inputProps={{ inputMode: "numeric", maxLength: 6, autoComplete: "one-time-code" }}
              label="Verification code"
              fullWidth
              value={code}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(v);
              }}
            />
          </MDBox>
          <MDBox mb={2} textAlign="right">
            <MDTypography
              component="button"
              type="button"
              variant="button"
              color={timer === 0 ? "info" : "text"}
              onClick={handleResend}
              disabled={timer !== 0 || resendLoading || !email}
              sx={{
                background: "none",
                border: "none",
                cursor: timer === 0 && !resendLoading ? "pointer" : "default",
                textDecoration: timer === 0 ? "underline" : "none",
                font: "inherit",
                padding: 0,
              }}
            >
              Resend code
              {timer > 0 ? ` in (${String(timer).padStart(2, "0")}s)` : ""}
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="password"
              label="New password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              type="password"
              label="Confirm new password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {passwordsMismatch ? (
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                Passwords do not match
              </MDTypography>
            ) : null}
          </MDBox>
          <MDBox mt={3} mb={1} display="flex" gap={1}>
            <MDButton
              component={Link}
              to="/authentication/forgot-password"
              variant="outlined"
              color="dark"
              fullWidth
            >
              Back
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={disabled}
              onClick={handleChangePassword}
            >
              {loading ? "Saving…" : "Change password"}
            </MDButton>
          </MDBox>
          <MDBox mt={2} textAlign="center">
            <MDTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="info"
              fontWeight="medium"
              sx={{ textDecoration: "none" }}
            >
              Back to sign in
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
      <MDSnackbar
        color="error"
        icon="warning"
        title={errorMessage}
        dateTime=""
        content=""
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        close={() => setErrorOpen(false)}
        bgWhite
      />
      <MDSnackbar
        color="success"
        icon="check"
        title="Your password was updated. You can sign in now."
        dateTime=""
        content=""
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        close={() => setSuccessOpen(false)}
        bgWhite
      />
    </BasicLayout>
  );
}

export default ForgotPasswordConfirm;
